/**
 * ============================================================================
 * ANALYTICS SERVICE - Frontend
 * ============================================================================
 * 
 * Frontend service for analytics and reporting:
 * - Transaction analytics
 * - Wallet analytics
 * - Dispute analytics
 * - Payment analytics
 * - User activity analytics
 */

import api from './api';

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    return error.response.data;
  }
  return { message: error.message || 'An error occurred' };
};

/**
 * Get transaction analytics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {string} filters.groupBy - Group by (day, week, month)
 * @param {string} filters.status - Transaction status filter
 * @returns {Promise<Object>} Transaction analytics
 */
export const getTransactionAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/transactions', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get wallet analytics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {string} filters.groupBy - Group by (day, week, month)
 * @returns {Promise<Object>} Wallet analytics
 */
export const getWalletAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/wallet', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute analytics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {string} filters.status - Dispute status filter
 * @returns {Promise<Object>} Dispute analytics
 */
export const getDisputeAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/disputes', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get payment analytics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {string} filters.paymentMethod - Payment method filter
 * @param {string} filters.status - Payment status filter
 * @returns {Promise<Object>} Payment analytics
 */
export const getPaymentAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/payments', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user activity analytics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {string} filters.groupBy - Group by (day, week, month)
 * @returns {Promise<Object>} User activity analytics
 */
export const getUserActivityAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/activity', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dashboard overview statistics
 * @param {Object} filters - Filter options
 * @param {string} filters.timeRange - Time range (7d, 30d, 90d, 1y)
 * @returns {Promise<Object>} Dashboard statistics
 */
export const getDashboardStats = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/dashboard', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/** Alias for getDashboardStats (used by useAnalytics hook) */
export const getDashboardAnalytics = getDashboardStats;

/**
 * Get revenue analytics
 * @param {Object} filters - Filter options
 * @param {string} filters.startDate - Start date (ISO format)
 * @param {string} filters.endDate - End date (ISO format)
 * @param {string} filters.groupBy - Group by (day, week, month)
 * @returns {Promise<Object>} Revenue analytics
 */
export const getRevenueAnalytics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/revenue', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get growth metrics
 * @param {Object} filters - Filter options
 * @param {string} filters.metric - Metric type (users, transactions, revenue)
 * @param {string} filters.period - Period (7d, 30d, 90d, 1y)
 * @returns {Promise<Object>} Growth metrics
 */
export const getGrowthMetrics = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/growth', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/** Alias for getGrowthMetrics (used by useAnalytics hook) */
export const getUserGrowthMetrics = getGrowthMetrics;

/**
 * Export analytics data
 * @param {Object} options - Export options
 * @param {string} options.type - Analytics type (transactions, wallet, disputes, payments)
 * @param {string} options.format - Export format (csv, json, xlsx)
 * @param {Object} options.filters - Filter options
 * @returns {Promise<Blob>} Exported file blob
 */
export const exportAnalytics = async (options) => {
  try {
    const response = await api.post('/analytics/export', options, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get real-time metrics
 * @returns {Promise<Object>} Real-time metrics
 */
export const getRealTimeMetrics = async () => {
  try {
    const response = await api.get('/analytics/realtime');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/** Alias (used by useAnalytics hook) */
export const getRealtimeMetrics = getRealTimeMetrics;

/**
 * Get comparison analytics (compare two time periods)
 * @param {Object} options - Comparison options
 * @param {string} options.metric - Metric to compare
 * @param {string} options.period1 - First period (ISO date range)
 * @param {string} options.period2 - Second period (ISO date range)
 * @returns {Promise<Object>} Comparison analytics
 */
export const getComparisonAnalytics = async (options) => {
  try {
    const response = await api.post('/analytics/compare', options);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get custom analytics (generic endpoint)
 * @param {string} endpoint - Analytics endpoint path
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} Custom analytics data
 */
export const getCustomAnalytics = async (endpoint, filters = {}) => {
  try {
    const path = endpoint.startsWith('/') ? endpoint : `/analytics/${endpoint}`;
    const response = await api.get(path, { params: filters });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getTransactionAnalytics,
  getWalletAnalytics,
  getDisputeAnalytics,
  getPaymentAnalytics,
  getUserActivityAnalytics,
  getDashboardStats,
  getDashboardAnalytics,
  getRevenueAnalytics,
  getGrowthMetrics,
  getUserGrowthMetrics,
  exportAnalytics,
  getRealTimeMetrics,
  getRealtimeMetrics,
  getComparisonAnalytics,
  getCustomAnalytics,
};
